import React, { useEffect, useState, useRef } from 'react';
import Modal from "react-modal";
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from "../../components/select";
import { useTranslation } from "react-i18next";

import useForm from '../../hooks/recommend/useForm';
import validate from "../../validation/recommendByValidationRules";
import LanguageSelector from '../../components/languageSelector';
import { useLanguage } from '../../context/languageContext';
import PreviewImage from '../../components/image/previewImage';
import { normalizeCoverImage , pastePlainText } from '../../helper';
import Button from '../../components/button';
import Spinner from '../../components/spinner';
import RecommendedBooksField from './recommendedBooksField';


const EditRecommendedBy = ({ isModalVisible, closeModal, onSuccess, categoryId }) => {
  const { t } = useTranslation();
  const { selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang } = useLanguage();

  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const blobUrlRef = useRef(null);
  const initializedImageRef = useRef(false);

  // Which recommender the form is showing, and which one was asked for. The
  // popup stays mounted, so without these a failed load, or a slow reply
  // overtaken by the next open, left the PREVIOUS person in the form — Navya
  // opened Chamath Palihapitiya and got Aniela Gregorek, and Update would have
  // written Aniela's details onto Chamath. The form only shows, and Update
  // only works, once the requested recommender has loaded.
  const requestedIdRef = useRef(null);
  const [loadedId, setLoadedId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Every OPEN loads the saved recommender, not only a change of id: the popup
  // stays mounted between opens, so reopening the same person after Cancel
  // showed the cancelled edits (a removed book still gone) as if saved.
  useEffect(() => {
    if (categoryId && isModalVisible) {
      requestedIdRef.current = String(categoryId);
      setLoadedId(null);
      setLoadError(null);
      resetAutoBooksWritten();
      initialLangRef.current = false;
      setSelectedLang(fallbackLang);
      fetchCategoryData(String(categoryId));
    }
  }, [categoryId, isModalVisible]);

  const isLoaded = Boolean(isModalVisible && loadedId && loadedId === String(categoryId));

  const handleEditCategory = async () => {
    // Never save a form that is not showing this recommender.
    if (!isLoaded) return;
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/update/${categoryId}`, { values }, { headers });

      if (response.status) {
        onSuccess();
        closeModal();
        toast.success("Recommender updated successful!");
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("handleEditCategory catch error::", error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const [particularData, setParticularData] = useState([]);

  const {
    values,
    setValues,
    errors,
    isSubmit,
    setIsSubmit,
    editMediaError,
    languages,
    socialMediaLinks,
    recommendeGenres,
    recommendeBooks,
    fieldOptions,
    
    handleGetTranslationByLang,
    handleSelectLanguage,
    handleUpdateTranslationField,
    handleUpdateRecommendedBooks,
    handleUpdateBooksWritten,
    handleAddMediaLinkValues,
    handleUploadMedia,
    handleSubmit,
    resetAutoBooksWritten,
  } = useForm(handleEditCategory, validate, adminInfo);

  const fetchCategoryData = async (id) => {
    if (!id) return;
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/get/${id}`, {}, { headers });

      // A reply for an earlier open is dropped: another recommender has been
      // asked for since.
      if (requestedIdRef.current !== id) return;
      const record = response?.data;
      if (response?.status && record && (!record._id || String(record._id) === id)) {
        setParticularData(record);
      } else {
        setLoadError("This recommender could not be loaded. Close and try again.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (requestedIdRef.current !== id) return;
      const message = error.response?.data?.message || error.response?.data?.error;
      setLoadError(`This recommender could not be loaded${message ? ` (${message})` : ""}. Close and try again.`);
    }
  };

  useEffect(() => {
    if (particularData && particularData.translations && !initialLangRef.current) {
      const mergedTranslations = languages.map((lang) => {
        return (
          particularData.translations.find((t) => t.langCode === lang) || {
            langCode: lang,
            name: particularData?.name || "",
            about: particularData?.about || "",
            type: particularData?.type || "NonFiction",
            profession: particularData?.profession || [],
            recommendedBooks: particularData?.recommendedBooks || [],
            recommendedGenres: particularData?.recommendedGenres || [],
            booksWritten: particularData?.booksWritten || [],
          }
        );
      });

      // Normalize image: convert S3 URLs to CloudFront URLs and ensure consistent format
      const normalizedImage = normalizeCoverImage(particularData.image);

      setValues((prevValues) => ({
        ...prevValues,
        image: normalizedImage,
        mediaLink: particularData.mediaLink || [],
        translations: mergedTranslations
      }));

      // Initialize preview image URL ONCE from backend image
      if (!initializedImageRef.current && normalizedImage?.uploadedImageUrl) {
        setPreviewImageUrl(normalizedImage.uploadedImageUrl);
        initializedImageRef.current = true;
      }

      initialLangRef.current = true;
      // The record's own id, so the form is marked as showing exactly what it
      // holds — and Update only unlocks if that is who was asked for.
      setLoadedId(String(particularData._id || requestedIdRef.current));
    }
  }, [particularData, languages]);

  // Handle local preview when file is selected - single state update, instant preview
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];

    // Clean up previous blob URL if exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // Generate local preview URL immediately (synchronous, no await)
    if (file && file.type.startsWith('image/')) {
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewImageUrl(blobUrl); // Single state update - instant preview
    } else {
      // If no file, revert to existing image if available
      const existingImageUrl = values.image?.uploadedImageUrl || null;
      setPreviewImageUrl(existingImageUrl);
    }

    // Call upload handler (async, but doesn't block preview)
    handleUploadMedia(event);
  };

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Reset preview when modal closes
  useEffect(() => {
    if (!isModalVisible) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPreviewImageUrl(null);
      initializedImageRef.current = false;
      // Dropped so the next open's response is applied even when it is
      // identical to this one.
      setParticularData([]);
      requestedIdRef.current = null;
      setLoadedId(null);
      setLoadError(null);
    }
  }, [isModalVisible]);


  return (
    <Modal
      isOpen={isModalVisible}
      onRequestClose={closeModal}
      className="modal_body_lg"
      overlayClassName="modal_overlay"
    >
      <div className="box_wrapper">
        <div className="d-flex justify-content-between fw-bold mb-3">
          <h3>Edit Recommender</h3>
          <LanguageSelector onSelectLanguage={handleSelectLanguage} />
        </div>
        {!isLoaded ? (
          <div className="modal_content_lg d-flex align-items-center justify-content-center" style={{ minHeight: "240px" }}>
            {loadError ? <p className="error-message d-block m-0">{loadError}</p> : <Spinner />}
          </div>
        ) : (
        <div className="modal_content_lg">
          <div className='container-fluid my-2'>
            <div className='row'>
              <div className='col-sm-12 col-md-7'>
                <div className="mb-3">
                  <label className="label">{t("recommender.recommenderName")}:</label>
                  <input
                    className="form_control"
                    placeholder={`${t("general.enter")} ${t("recommender.recommenderName")}...`}
                    type="text"
                    name="name"
                    value={handleGetTranslationByLang(selectedLang).name}
                    onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                  />
                  {errors && errors.name && <p className="error-message d-block">{errors.name}</p>}
                </div>
                <div className="mb-3">
                  <label className="label">{t("recommender.recommendedGenres")}:</label>
                  <Select
                    className="form-control-md fw-medium p-0"
                    isMulti
                    closeMenuOnSelect={false}
                    name="recommendedGenres"
                    placeholder={`Select ${t(`general.genre`)}...`}
                    value={handleGetTranslationByLang(selectedLang)?.recommendedGenres?.map((genre) => ({ label: genre.genreName, value: genre.genreId })) || []}
                    options={recommendeGenres?.filter((genre) => genre.type === handleGetTranslationByLang(selectedLang)?.type) || []}
                    isDisabled={true}
                  />
                  {errors && errors.recommendedGenres && <p className="error-message d-block">{errors.recommendedGenres}</p>}
                </div>
                <div className="mb-3">
                  <label className="label">{t("recommender.professionField")}:</label>
                  <Select
                    className="form-control-md fw-medium p-0"
                    isMulti
                    closeMenuOnSelect={false}
                    name="profession"
                    onChange={(selectedOptions) => {
                      const updatedGenres = selectedOptions.map((option) => ({
                        professionName: option.label,
                        professionId: option.value,
                      }));
                      handleUpdateTranslationField("profession", updatedGenres, selectedLang);
                    }}
                    value={handleGetTranslationByLang(selectedLang)?.profession?.map((option) => ({ label: option.professionName, value: option.professionId })) || []}
                    options={fieldOptions}
                  />
                  {errors && errors.profession && <p className="error-message d-block">{errors.profession}</p>}
                </div>
              </div>
              <div className="col-sm-12 col-md-5">
                <label className="label">{t("recommender.socialMediaLink")}</label>
                <div className="media_link">
                  {socialMediaLinks.map((item, index) => {
                    const socialLink = values.mediaLink?.find(link => link.key.toLowerCase() === item.key.toLowerCase());

                    return (
                      <div key={index} className="d-flex gap-2 align-items-center my-3 px-3">
                        <img src={item.imgSrc} alt={item.alt} />
                        <input
                          className="form_control"
                          placeholder={`Enter ${item.alt} Link...`}
                          type="text"
                          name="mediaLink"
                          value={socialLink?.link}
                          onChange={(e) => handleAddMediaLinkValues(e.target.value, item.key)}
                        />
                      </div>
                    );
                  })}
                </div>
                {errors && errors.mediaLink && <p className="error-message d-block">{errors.mediaLink}</p>}
              </div>
            </div>
          </div>
          <div className="row my-2 px-3">
            <div className="col-12">
              <RecommendedBooksField
                items={handleGetTranslationByLang(selectedLang)?.recommendedBooks || []}
                bookOptions={recommendeBooks}
                token={adminInfo?.token}
                onChange={(nextBooks) => handleUpdateRecommendedBooks(nextBooks, selectedLang)}
                error={errors?.recommendedBooks}
              />
              <RecommendedBooksField
                label={t("recommender.booksWritten")}
                hint="Their books and series, in this order. A series shows on their profile with their series."
                items={handleGetTranslationByLang(selectedLang)?.booksWritten || []}
                bookOptions={recommendeBooks}
                token={adminInfo?.token}
                onChange={handleUpdateBooksWritten}
              />
            </div>
          </div>
          <div className="row my-2 px-3">
          <div className="col-sm-12 col-md-8 mb-3">
            <label className="label">{t("general.about")}:
              {" "}<span className="text-muted small fst-italic mt-1">(Please write about the recommender within given characters — {handleGetTranslationByLang(selectedLang).about?.length || 0}/4500)</span>
            </label>
            <textarea
              className="form_control"
              placeholder={`${t("general.enter")} ${t("general.about")}...`}
              type="text"
              name="about"
              maxLength={4500}
              value={handleGetTranslationByLang(selectedLang).about}
              onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
              style={{ height: "330px" }}
            // Pasted markdown markers ("**The Internet of Money**") are stripped:
            // this field stores plain text, so they would be saved and shown
            // literally.
            onPaste={(event) =>
              pastePlainText(event, (value) =>
                handleUpdateTranslationField("about", value, selectedLang)
              )
            }
            />
            {errors && errors.about && <p className="error-message d-block">{errors.about}</p>}
          </div>
          <div className="col-sm-12 col-md-4 mb-3">
            <PreviewImage
              label={t("general.coverImage")}
              image={previewImageUrl}
              mediaError={editMediaError}
              error={errors?.image}
              uploadMedia={handleFileSelect}
              />
            </div>
          </div>
        </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
          <Button onClick={handleSubmit} disabled={isSubmit || !isLoaded} isSubmit={isSubmit} label="Updating ..." text="Update" />
        </div>
      </div>
    </Modal>
  )
}

export default EditRecommendedBy

